<script lang="ts">
  /**
   * ScanReceiptModal — Digitaliza nota/cupom/lista com Gemini Vision
   *
   * Estados:
   *   capture → preview → analyzing → confirm → saving → (fecha com sucesso)
   *                           ↘ error_image (ilegível → retry)
   *                           ↘ error_ai   (API indisponível → retry)
   */
  import { api } from "$lib/api/client";
  import { categories } from "$lib/stores/categories.svelte";
  import { accounts } from "$lib/stores/accounts.svelte";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { todayISO } from "$lib/utils/format";
  import { router } from "$lib/router.svelte";
  import ReceiptItemList from "$lib/components/ReceiptItemList.svelte";
  import type { ConfirmItem } from "$lib/components/ReceiptItemList.svelte";

  let { onCreated, open = $bindable(false) } = $props<{
    onCreated: () => void;
    open?: boolean;
  }>();

  // ── State machine ─────────────────────────────────────────────────────────
  type Step = "capture" | "preview" | "analyzing" | "confirm" | "saving" | "error_image" | "error_ai";
  let step = $state<Step>("capture");

  // ── Photo ──────────────────────────────────────────────────────────────────
  let photoUrl = $state<string | null>(null);
  let photoBase64 = $state<string>("");
  let photoMime = $state<string>("image/jpeg");
  let fileCameraInput = $state<HTMLInputElement | null>(null);
  let fileGalleryInput = $state<HTMLInputElement | null>(null);

  // ── API response ───────────────────────────────────────────────────────────
  interface ParsedTransaction {
    amount: number;
    description: string;
    due_date: string | null;
    kind: string;
    category: string;
    confidence: number;
  }
  interface ReceiptResult {
    found: boolean;
    transactions: ParsedTransaction[];
    overall_confidence: number;
  }

  // ── Confirm list (editable) ────────────────────────────────────────────────

  let confirmItems = $state<ConfirmItem[]>([]);
  let sharedAccountId = $state<string | null>(null);

  // Derived summary
  let selectedItems = $derived(confirmItems.filter((i) => i.selected));
  let selectedCount = $derived(selectedItems.length);
  let selectedTotal = $derived(
    selectedItems.reduce((s, i) => s + (Number(i.amount.replace(",", ".")) || 0), 0),
  );

  // ── Saving progress ────────────────────────────────────────────────────────
  let savingProgress = $state(0);
  let savingTotal = $state(0);
  let savingPct = $derived(savingTotal > 0 ? Math.round((savingProgress / savingTotal) * 100) : 0);

  // ── Analyzing animation ────────────────────────────────────────────────────
  const ANALYZING_MSGS = [
    "Lendo sua nota…",
    "Identificando lançamentos…",
    "Reconhecendo valores…",
    "Classificando categorias…",
    "Quase lá…",
  ];
  let analyzingMsgIdx = $state(0);
  let analyzingInterval: ReturnType<typeof setInterval> | null = null;

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  $effect(() => {
    if (open) {
      reset();
      categories.load();
      accounts.load();
    } else {
      stopAnalyzingAnimation();
    }
  });

  function reset() {
    step = "capture";
    photoUrl = null;
    photoBase64 = "";
    confirmItems = [];
    sharedAccountId = null;
    savingProgress = 0;
    savingTotal = 0;
    stopAnalyzingAnimation();
  }

  function close() {
    open = false;
    reset();
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape" && open && step !== "saving") close();
  }

  // ── Photo selection & compression ─────────────────────────────────────────
  async function handleFileSelected(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    (e.target as HTMLInputElement).value = "";
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    photoUrl = URL.createObjectURL(file);
    step = "preview";
    try {
      const compressed = await compressImage(file);
      photoBase64 = compressed.base64;
      photoMime = compressed.mimeType;
    } catch {
      photoBase64 = await readAsBase64(file);
      photoMime = file.type || "image/jpeg";
    }
  }

  function compressImage(file: File): Promise<{ base64: string; mimeType: string }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const blobUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(blobUrl);
        // 1400px para capturar bem escritas à mão (mais detalhe)
        const MAX = 1400;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round((h * MAX) / w); w = MAX; }
          else { w = Math.round((w * MAX) / h); h = MAX; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas indisponível")); return; }
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error("Falha na compressão")); return; }
            const reader = new FileReader();
            reader.onload = () => resolve({ base64: (reader.result as string).split(",")[1], mimeType: "image/jpeg" });
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          "image/jpeg",
          0.85,
        );
      };
      img.onerror = reject;
      img.src = blobUrl;
    });
  }

  function readAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ── Analysis ──────────────────────────────────────────────────────────────
  async function analyze() {
    if (!photoBase64) {
      toasts.warn("Aguarde", "A imagem ainda está sendo preparada. Tente novamente.");
      return;
    }
    step = "analyzing";
    startAnalyzingAnimation();

    try {
      const res = await api<ReceiptResult>("/ai/parse-receipt", {
        method: "POST",
        body: JSON.stringify({ image_base64: photoBase64, mime_type: photoMime }),
      });
      stopAnalyzingAnimation();

      if (!res.found || !res.transactions?.length) {
        step = "error_image";
        return;
      }

      await categories.ensureSeeded();

      confirmItems = res.transactions.map((tx, idx): ConfirmItem => {
        const k = tx.kind === "income" ? "income" : "expense";
        const cat = categories.byKind(k).find(
          (c) => c.name.toLowerCase() === tx.category.toLowerCase(),
        );
        return {
          _id: idx,
          selected: tx.confidence >= 0.35,
          kind: k,
          amount: String(tx.amount).replace(".", ","),
          description: tx.description,
          due_date: tx.due_date ?? todayISO(),
          installments: 1,
          category_id: cat?.id ?? null,
          confidence: tx.confidence,
        };
      });

      if (accounts.list.length === 1) sharedAccountId = accounts.list[0].id;

      step = "confirm";
    } catch (err: any) {
      stopAnalyzingAnimation();
      if (err?.status === 401) {
        close();
        toasts.warn("Sessão expirada", "Faça login novamente para continuar.");
        router.go("/login");
        return;
      }
      step = err?.status === 503 ? "error_ai" : "error_image";
    }
  }

  function startAnalyzingAnimation() {
    analyzingMsgIdx = 0;
    analyzingInterval = setInterval(() => {
      analyzingMsgIdx = (analyzingMsgIdx + 1) % ANALYZING_MSGS.length;
    }, 1800);
  }

  function stopAnalyzingAnimation() {
    if (analyzingInterval) { clearInterval(analyzingInterval); analyzingInterval = null; }
  }

  // ── Save all selected ─────────────────────────────────────────────────────
  async function saveAll() {
    if (!sharedAccountId) {
      toasts.warn("Conta obrigatória", "Selecione uma conta antes de salvar.");
      return;
    }
    const toSave = selectedItems;
    if (toSave.length === 0) {
      toasts.warn("Nenhum selecionado", "Marque pelo menos um lançamento.");
      return;
    }

    step = "saving";
    savingProgress = 0;
    savingTotal = toSave.length;

    let savedCount = 0;
    let failedCount = 0;

    for (const item of toSave) {
      const amountNum = Number(item.amount.replace(",", "."));
      if (!amountNum || amountNum <= 0 || !item.description.trim() || !item.category_id) {
        failedCount++;
        savingProgress++;
        continue;
      }
      try {
        const txBase = {
          account_id: sharedAccountId,
          category_id: item.category_id,
          kind: item.kind,
          status: "pending",          // sempre não pago ao importar por foto
          amount: amountNum.toFixed(2),
          currency: "BRL",
          date: todayISO(),            // data de lançamento = hoje
          due_date: item.due_date || null,  // data de vencimento da nota
          description: item.description.trim(),
        };

        if (item.installments >= 2) {
          // Criar N parcelas mensais via endpoint dedicado
          await api("/transactions/installments", {
            method: "POST",
            body: JSON.stringify({
              template: txBase,
              count: item.installments,
              frequency: "monthly",
              interval: 1,
            }),
          });
        } else {
          await api("/transactions", {
            method: "POST",
            body: JSON.stringify(txBase),
          });
        }
        savedCount++;
      } catch {
        failedCount++;
      }
      savingProgress++;
    }

    if (savedCount > 0) {
      toasts.success(
        `${savedCount} lançamento${savedCount > 1 ? "s" : ""} criado${savedCount > 1 ? "s" : ""}!`,
        failedCount > 0 ? `${failedCount} não puderam ser salvos — verifique os campos.` : "",
      );
      onCreated();
      close();
    } else {
      toasts.error("Nenhum salvo", "Verifique se todos os campos obrigatórios estão preenchidos.");
      step = "confirm";
    }
  }

  function fmtCurrency(v: string) {
    const n = Number(v.replace(",", "."));
    if (isNaN(n) || n === 0) return "R$ –";
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }
</script>

<svelte:window onkeydown={onKey} />

<!-- Hidden file inputs -->
<input
  type="file"
  accept="image/*"
  capture="environment"
  class="hidden"
  bind:this={fileCameraInput}
  onchange={handleFileSelected}
/>
<input
  type="file"
  accept="image/*"
  class="hidden"
  bind:this={fileGalleryInput}
  onchange={handleFileSelected}
/>

{#if open}
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
    style="background: rgba(0,0,0,0.55); backdrop-filter: blur(2px);"
    role="dialog"
    aria-modal="true"
    aria-label="Digitalizar nota"
  >
    <!-- Click backdrop to close (only on capture/error steps) -->
    {#if step === "capture" || step === "error_image" || step === "error_ai"}
      <button
        type="button"
        class="absolute inset-0 w-full h-full cursor-default"
        aria-label="Fechar"
        onclick={close}
      ></button>
    {/if}

    <!-- Panel — slides up on mobile, centered on desktop -->
    <div
      class="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden"
      style="background: var(--surface); max-height: 95dvh; overflow-y: auto;"
    >

      <!-- ── CAPTURE ─────────────────────────────────────────────────────── -->
      {#if step === "capture"}
        <div class="p-6 flex flex-col items-center text-center gap-5">
          <!-- Close button -->
          <button type="button" class="absolute top-4 right-4 btn-ghost text-lg leading-none" onclick={close} aria-label="Fechar">✕</button>

          <div class="text-6xl mt-4" aria-hidden="true">📸</div>
          <div>
            <h2 class="text-xl font-bold">Digitalizar nota</h2>
            <p class="text-sm mt-1" style="color: var(--text-muted);">
              Funciona com cupons fiscais, notas de compras,<br>
              comprovantes e listas escritas à mão.
            </p>
          </div>

          <div class="w-full flex flex-col gap-3 mt-1">
            <button
              type="button"
              class="btn-primary w-full text-base py-3 flex items-center justify-center gap-2"
              onclick={() => fileCameraInput?.click()}
            >
              📷 Tirar foto agora
            </button>
            <button
              type="button"
              class="btn-secondary w-full text-base py-3 flex items-center justify-center gap-2"
              onclick={() => fileGalleryInput?.click()}
            >
              🖼️ Escolher da galeria
            </button>
          </div>

          <!-- Tips -->
          <div class="w-full rounded-xl p-4 text-left text-sm space-y-1" style="background: var(--surface-alt);">
            <p class="font-semibold mb-2" style="color: var(--text-muted);">Dicas para melhores resultados</p>
            <p>✅ Coloque a nota em superfície plana</p>
            <p>✅ Boa iluminação, sem sombras</p>
            <p>✅ Câmera bem próxima, texto legível</p>
            <p>✅ Nota sem dobras ou amassados</p>
          </div>
        </div>

      <!-- ── PREVIEW ─────────────────────────────────────────────────────── -->
      {:else if step === "preview"}
        <div class="flex flex-col gap-0">
          <!-- Photo -->
          {#if photoUrl}
            <img src={photoUrl} alt="Nota selecionada" class="w-full object-cover max-h-64 sm:max-h-80" />
          {/if}

          <div class="p-5 flex flex-col gap-3">
            <div>
              <h2 class="text-lg font-bold">Esta foto está boa?</h2>
              <p class="text-sm mt-1" style="color: var(--text-muted);">
                Verifique se o texto está legível e a nota está inteira no quadro.
              </p>
            </div>

            <button
              type="button"
              class="btn-primary w-full py-3 text-base flex items-center justify-center gap-2"
              onclick={analyze}
            >
              🔍 Analisar com IA
            </button>

            <div class="flex gap-2">
              <button
                type="button"
                class="btn-secondary flex-1 py-2"
                onclick={() => fileCameraInput?.click()}
              >
                📷 Tirar outra
              </button>
              <button
                type="button"
                class="btn-secondary flex-1 py-2"
                onclick={() => fileGalleryInput?.click()}
              >
                🖼️ Trocar imagem
              </button>
            </div>

            <button type="button" class="btn-ghost text-sm text-center" onclick={close}>
              Cancelar
            </button>
          </div>
        </div>

      <!-- ── ANALYZING ───────────────────────────────────────────────────── -->
      {:else if step === "analyzing"}
        <div class="flex flex-col gap-0">
          <!-- Photo com overlay animado -->
          {#if photoUrl}
            <div class="relative">
              <img src={photoUrl} alt="Analisando…" class="w-full object-cover max-h-64 sm:max-h-80 opacity-40" />
              <div class="absolute inset-0 flex items-center justify-center">
                <div class="rounded-2xl px-5 py-4 text-center" style="background: var(--surface); box-shadow: 0 4px 32px rgba(0,0,0,0.3);">
                  <!-- Spinner -->
                  <div class="flex justify-center mb-3">
                    <svg class="animate-spin h-8 w-8 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  </div>
                  <p class="font-semibold text-sm">{ANALYZING_MSGS[analyzingMsgIdx]}</p>
                  <p class="text-xs mt-1" style="color: var(--text-muted);">Powered by Gemini AI</p>
                </div>
              </div>
            </div>
          {/if}
          <div class="p-5">
            <p class="text-sm text-center" style="color: var(--text-muted);">
              Aguarde enquanto a IA lê sua nota…
            </p>
          </div>
        </div>

      <!-- ── CONFIRM: lista editável ────────────────────────────────────── -->
      {:else if step === "confirm"}
        <ReceiptItemList
          bind:items={confirmItems}
          bind:sharedAccountId
          selectedCount={selectedCount}
          selectedTotal={selectedTotal}
          onSave={saveAll}
          onClose={close}
        />

      <!-- ── SAVING: barra de progresso ─────────────────────────────────── -->
      {:else if step === "saving"}
        <div class="p-8 flex flex-col items-center gap-5 text-center">
          <svg class="animate-spin h-10 w-10 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <div>
            <p class="font-semibold text-base">Salvando seus lançamentos…</p>
            <p class="text-sm mt-1" style="color: var(--text-muted);">
              {savingProgress} de {savingTotal}
            </p>
          </div>
          <!-- Barra de progresso -->
          <div class="w-full rounded-full h-2.5" style="background: var(--border);">
            <div
              class="h-2.5 rounded-full transition-all duration-300"
              style="background: var(--brand); width: {savingPct}%;"
            ></div>
          </div>
          <p class="text-xs" style="color: var(--text-muted);">Não feche o app</p>
        </div>

      <!-- ── ERROR: imagem ilegível ──────────────────────────────────────── -->
      {:else if step === "error_image"}
        <div class="p-6 flex flex-col items-center text-center gap-5">
          <div class="text-5xl mt-2" aria-hidden="true">🔍</div>
          <div>
            <h2 class="text-xl font-bold">Não consegui ler essa nota</h2>
            <p class="text-sm mt-1" style="color: var(--text-muted);">
              A imagem pode estar escura, distante ou fora de foco.
            </p>
          </div>

          <div class="w-full rounded-xl p-4 text-left text-sm space-y-1" style="background: var(--surface-alt);">
            <p class="font-semibold mb-2" style="color: var(--text-muted);">O que pode ajudar</p>
            <p>💡 Mais luz — use a lanterna do celular</p>
            <p>📐 Câmera perpendicular à nota (sem ângulo)</p>
            <p>🔎 Aproxime mais até preencher a tela</p>
            <p>📄 Evite reflexos e sombras sobre o texto</p>
          </div>

          <div class="w-full flex flex-col gap-2">
            <button
              type="button"
              class="btn-primary w-full py-3"
              onclick={() => { step = "capture"; }}
            >
              📷 Tentar de novo
            </button>
            <button type="button" class="btn-ghost text-sm" onclick={close}>Cancelar</button>
          </div>
        </div>

      <!-- ── ERROR: IA indisponível ──────────────────────────────────────── -->
      {:else if step === "error_ai"}
        <div class="p-6 flex flex-col items-center text-center gap-5">
          <div class="text-5xl mt-2" aria-hidden="true">🤖</div>
          <div>
            <h2 class="text-xl font-bold">IA temporariamente indisponível</h2>
            <p class="text-sm mt-1" style="color: var(--text-muted);">
              O serviço está sobrecarregado. Aguarde alguns segundos e tente novamente.
            </p>
          </div>
          <div class="w-full flex flex-col gap-2">
            <button
              type="button"
              class="btn-primary w-full py-3"
              onclick={() => { step = "preview"; }}
            >
              🔄 Tentar novamente
            </button>
            <button type="button" class="btn-ghost text-sm" onclick={close}>Cancelar</button>
          </div>
        </div>
      {/if}

    </div>
  </div>
{/if}
